/**
 * AddPhotoButton — file picker + upload trigger. Uses `useUploadPhoto` to
 * POST a multipart form to `/api/photos`. Attaches the photo to exactly one
 * of `placeId` xor `visitId` (mirrors the backend XOR constraint).
 *
 * Pipeline:
 *   user taps button → file picker → `compressImage` → `useUploadPhoto`.
 * On error we surface the backend message via toast (the Worker returns
 * specific codes for TOO_LARGE, INVALID_MIME, VALIDATION).
 *
 * Per AC-063 / AC-064: input[type=file] accepts `image/*` and includes
 * `capture="environment"` on mobile so the system camera can be the
 * direct source.
 */
import * as React from 'react';
import { Camera } from '@phosphor-icons/react';
import type { PlaceId, VisitId } from '@shared/types';
import { Button } from './ui/Button';
import { compressImage } from '../lib/image';
import { useActiveHouseholdId } from '../lib/household';
import { useUploadPhoto } from '../lib/queries';
import { useToast } from '../lib/store';
import { useErrorToasts } from '../lib/errorToast';
import { cn } from '../lib/cn';

export interface AddPhotoButtonProps {
  /** Attach the photo to a place. Mutually exclusive with `visitId`. */
  placeId?: PlaceId;
  /** Attach the photo to a visit. Mutually exclusive with `placeId`. */
  visitId?: VisitId;
  /** Optional className for the outer wrapper. */
  className?: string;
  label?: string;
}

export function AddPhotoButton({
  placeId,
  visitId,
  className,
  label = 'Add photo',
}: AddPhotoButtonProps) {
  const householdId = useActiveHouseholdId();
  const upload = useUploadPhoto(householdId);
  const toast = useToast();
  const errors = useErrorToasts();
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [compressing, setCompressing] = React.useState(false);
  const [compressFallback, setCompressFallback] = React.useState(false);

  async function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Reset the input so the same file can be chosen again after an error.
    e.target.value = '';
    if (!file) return;

    setCompressing(true);
    const result = await compressImage(file);
    setCompressing(false);
    if (result.fallback) setCompressFallback(true);
    else setCompressFallback(false);

    try {
      await upload.mutateAsync({
        file: result.file,
        ...(placeId ? { place_id: placeId } : {}),
        ...(visitId ? { visit_id: visitId } : {}),
      });
      toast.success('Photo added.');
    } catch (err) {
      if (!errors.handle(err)) {
        toast.error(
          err instanceof Error ? err.message : "We couldn't add that photo.",
        );
      }
    }
  }

  const busy = compressing || upload.isPending;

  return (
    <div className={className} data-testid="add-photo">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/*"
        capture="environment"
        onChange={onFileChange}
        className="sr-only"
        data-testid="add-photo-input"
      />
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        loading={busy}
        loadingLabel={compressing ? 'Slimming…' : 'Uploading…'}
      >
        <Camera size={14} weight="regular" aria-hidden />
        {label}
      </Button>
      {compressFallback ? (
        <p
          className={cn(
            'mt-2 font-serif italic text-xs text-ink-500 leading-snug',
          )}
        >
          Your browser couldn&apos;t slim this photo. We&apos;ll upload the
          original.
        </p>
      ) : null}
    </div>
  );
}
